import { useEffect, useRef } from "react";
import * as THREE from "three";
import { STORY_CARD_W as CARD_W, StoryCard } from "./StoryCard";

// The focused painting: a breathing glow along its frame, a "Step inside ✦" invitation that fades into that glow,
// and a small story card beside it (never over it; it rests when there is no room beside or below).
export function StepNudge({ mesh, painting, subtle, onOpen }) {
  const glow = useRef(null);
  const btn = useRef(null);
  const card = useRef(null);
  useEffect(() => {
    const b = new THREE.Box3();
    const v = new THREE.Vector3();
    let raf;
    const tick = () => {
      const cam = window.__afaeGL?.camera;
      const m = mesh();
      if (cam && m && glow.current) {
        b.setFromObject(m);
        let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity, behind = false;
        for (let i = 0; i < 8; i++) {
          v.set(i & 1 ? b.max.x : b.min.x, i & 2 ? b.max.y : b.min.y, i & 4 ? b.max.z : b.min.z).project(cam);
          if (v.z > 1) behind = true;
          const sx = ((v.x + 1) / 2) * window.innerWidth;
          const sy = ((1 - v.y) / 2) * window.innerHeight;
          x0 = Math.min(x0, sx);
          x1 = Math.max(x1, sx);
          y0 = Math.min(y0, sy);
          y1 = Math.max(y1, sy);
        }
        const W = window.innerWidth;
        const H = window.innerHeight;
        const off = behind || x1 < 0 || x0 > W || y1 < 0 || y0 > H;
        Object.assign(glow.current.style, { left: `${x0}px`, top: `${y0}px`, width: `${x1 - x0}px`, height: `${y1 - y0}px`, opacity: off ? "0" : "" });
        if (btn.current) {
          const y = Math.max(y0 + 34, 120);
          btn.current.style.transform = `translate(${(x0 + x1) / 2}px, ${y}px) translate(-50%, -50%)`;
          btn.current.style.visibility = off ? "hidden" : "visible";
        }
        if (card.current) {
          const c = card.current;
          const ch = c.offsetHeight;
          const right = W - x1 - 28 >= CARD_W;
          const left = x0 - 28 >= CARD_W;
          const below = H - y1 - 96 >= ch && W >= CARD_W + 24;
          let pos = null;
          if (right) pos = [x1 + 24, Math.min(Math.max(y0, 90), H - ch - 90)];
          else if (left) pos = [x0 - 24 - CARD_W, Math.min(Math.max(y0, 90), H - ch - 90)];
          else if (below) pos = [Math.min(Math.max((x0 + x1) / 2 - CARD_W / 2, 12), W - CARD_W - 12), y1 + 14];
          // a painting wide enough to fill the view: the opaque label sits low at the side, still legible
          else pos = [Math.max(W - CARD_W - 24, 12), H - ch - 96];
          c.style.transform = pos ? `translate(${pos[0]}px, ${pos[1]}px)` : "";
          c.style.opacity = !off && pos ? "" : "0";
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [mesh]);
  return (
    <>
      <div ref={glow} data-testid="step-nudge-glow" className="step-glow pointer-events-none fixed z-[6] rounded-[3px]" />
      {!subtle && (
        <button ref={btn} data-testid="step-nudge-button" onClick={() => onOpen(painting)} className="step-invite fixed left-0 top-0 z-[41] whitespace-nowrap rounded-full border border-white/70 bg-[#FBF7F2]/80 px-4 py-1 font-display text-base italic text-[#2D1E3E] shadow-[0_10px_30px_-12px_rgba(91,58,133,0.55)] backdrop-blur-md transition-colors duration-200 hover:bg-white">
          Step inside ✦
        </button>
      )}
      <StoryCard key={painting.id} painting={painting} cardRef={card} style={{ opacity: 0 }} className="pointer-events-none fixed left-0 top-0 z-[30] transition-opacity duration-500" />
    </>
  );
}
